import { useState, useEffect } from "react";
import { Lock, KeyRound, Fingerprint, Loader2, ShieldOff, Info } from "lucide-react";
import { toast } from "sonner";

export default function VaultUnlock({ vault, onUnlocked }) {
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [hint, setHint] = useState("");
  const [confirmingWipe, setConfirmingWipe] = useState(false);

  useEffect(() => { vault.getHint().then((h) => h && setHint(h)); }, [vault]);

  // Auto-attempt biometric unlock on mount if enrolled + available
  useEffect(() => {
    if (vault.biometricAvailable && vault.biometricEnrolled) {
      vault.unlockBio().then(() => onUnlocked && onUnlocked()).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vault.biometricAvailable, vault.biometricEnrolled]);

  const submit = async () => {
    if (!pin) return;
    setBusy(true);
    try { await vault.unlockPin(pin); toast.success("Vault unlocked"); onUnlocked && onUnlocked(); }
    catch (e) { toast.error(e.message || "Incorrect PIN"); setPin(""); }
    setBusy(false);
  };

  const bio = async () => {
    setBusy(true);
    try { await vault.unlockBio(); toast.success("Unlocked"); onUnlocked && onUnlocked(); }
    catch (e) { toast.error(e.message || "Biometric unlock failed"); }
    setBusy(false);
  };

  const wipe = async () => {
    setBusy(true);
    try { await vault.wipe(); toast.success("Vault wiped from this device"); setConfirmingWipe(false); }
    catch (e) { toast.error(e.message || "Wipe failed"); }
    setBusy(false);
  };

  return (
    <div className="max-w-sm mx-auto p-6 text-center" data-testid="vault-unlock">
      <div className="w-16 h-16 mx-auto rounded-full bg-slate-900 grid place-items-center">
        <Lock className="w-8 h-8 text-white" />
      </div>
      <h2 className="font-serif font-900 text-2xl mt-3">Private Vault</h2>
      <p className="text-xs text-slate-500 mt-1">Locked · on this device only</p>

      <div className="mt-6 space-y-3">
        <div className="text-left">
          <label className="block text-sm">
            <span className="font-600 text-slate-700">PIN or passphrase</span>
            <input autoFocus type="password" value={pin} onChange={(e) => setPin(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && submit()}
              data-testid="vault-unlock-pin"
              className="mt-1 w-full border-2 border-slate-300 focus:border-emerald-500 px-4 py-3 text-lg font-mono tracking-wider text-center rounded" />
          </label>
          {hint && (
            <div className="mt-2 text-[11px] text-slate-500 flex items-center justify-center gap-1">
              <Info className="w-3 h-3" /> Hint: {hint}
            </div>
          )}
        </div>

        <button onClick={submit} disabled={busy || !pin} data-testid="vault-unlock-submit"
          className="w-full py-3 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-700 rounded inline-flex items-center justify-center gap-2">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />} Unlock
        </button>

        {vault.biometricAvailable && vault.biometricEnrolled && (
          <button onClick={bio} disabled={busy} data-testid="vault-unlock-bio"
            className="w-full py-3 border-2 border-indigo-300 text-indigo-700 hover:bg-indigo-50 font-700 rounded inline-flex items-center justify-center gap-2">
            <Fingerprint className="w-4 h-4" /> Use biometric
          </button>
        )}
      </div>

      <div className="mt-8 pt-4 border-t border-slate-200">
        {!confirmingWipe ? (
          <button onClick={() => setConfirmingWipe(true)} data-testid="vault-unlock-wipe-request"
            className="text-[11px] text-slate-500 hover:text-rose-700 underline inline-flex items-center gap-1">
            <ShieldOff className="w-3 h-3" /> Forgot PIN? Wipe device vault
          </button>
        ) : (
          <div className="text-xs text-rose-800 space-y-2" data-testid="vault-unlock-wipe-confirm">
            <p className="font-700">This will delete <b>everything</b> on this device. Cannot be undone. Only proceed if you have a backup or accept the loss.</p>
            <div className="flex gap-2">
              <button onClick={() => setConfirmingWipe(false)} className="flex-1 py-2 border border-slate-300 rounded text-xs">Cancel</button>
              <button onClick={wipe} data-testid="vault-unlock-wipe-do"
                className="flex-1 py-2 bg-rose-600 text-white rounded text-xs font-700">Wipe device vault</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
